package com.siriusxm.pia.components

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.pia.SXMUI
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.minus
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.roundToLong
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.nanoseconds
import kotlin.time.Duration.Companion.seconds
import kotlin.time.DurationUnit
import kotlin.time.Instant

/**
 * A time range that can be either relative or absolute. Either
 * relative or (start,end) must be defined.
 */
data class TimeRange(
    /**
     * The relative duration
     */
    val relative: Period? = null,
    val absolute: OpenEndedRange<Instant>? = null,
    /**
     * When true, this range represents whole calendar days rather than an arbitrary span of time.
     * A relative period resolves to complete days — counted inclusively, so `1d` is today only and
     * `1w` is the trailing seven calendar days including today — bounded by wall-clock midnight and
     * end-of-day in the globally selected timezone. Absolute ranges are already stored as
     * day-boundary instants by [dateRangeSelect], so the flag only affects their display.
     *
     * Defaults to false, which preserves the original rolling-duration behavior.
     */
    val dayAligned: Boolean = false
) {
    init {
        if (relative == null && absolute == null) {
            throw IllegalArgumentException("Relative or absolute must be set")
        }
    }

    fun humanString(): String? {
        return relative?.humanString()
            ?: if (absolute != null) {
                // Render in the globally-selected timezone (header picker).
                val zoneId = currentTimeZoneId()
                if (dayAligned) {
                    // Time-of-day is an implementation detail of a day-aligned range; showing
                    // "00:00:00 - 23:59:59" would be noise.
                    "${absolute.start?.toLocalDateIn(zoneId) ?: ""} - ${absolute.end?.toLocalDateIn(zoneId) ?: ""}"
                } else {
                    "${absolute.start?.toLocalDateTimeIn(zoneId)?.toLocalDateTimeString() ?: ""} - ${absolute.end?.toLocalDateTimeIn(zoneId)?.toLocalDateTimeString() ?: ""}"
                }
            } else {
                null
            }
    }

    /**
     * Get the date range. For relative or incomplete ranges, the relativeTo parameter is used.
     */
    fun range(relativeTo: Instant = Clock.System.now()): ClosedRange<Instant> {
        if (relative != null) {
            if (dayAligned) {
                // Snap to whole calendar days in the globally selected timezone. The count is
                // inclusive of the current day, so a 3d period spans the day before yesterday
                // through the end of today.
                val zoneId = currentTimeZoneId()
                val endDate = relativeTo.toLocalDateIn(zoneId)
                val startDate = endDate.minus(relative.type.wholeDays * relative.count - 1, DateTimeUnit.DAY)
                return startDate.atStartOfDayInZone(zoneId)..endDate.atEndOfDayInZone(zoneId)
            }
            return relative.type.subtract(relativeTo, relative.count)..relativeTo
        } else if (absolute != null) {
            val start = absolute.start ?: Instant.DISTANT_PAST
            val end = absolute.end ?: relativeTo
            return start..end
        } else {
            throw IllegalStateException("one of relative or absolute must be set")
        }
    }

    /**
     * Determine the grouping amount. Attempting to organize around an
     * intelligent sections (i.e. hours, minutes, etc.)
     */
    fun groupingAmount(roughCount: Int): Duration {
        val range = range()
        val duration = range.endInclusive - range.start

        return roundGrouping(duration, roughCount)
    }

    /**
     * Get the duration of the time range
     */
    fun duration(): Duration {
        return range().let {
            it.endInclusive - it.start
        }
    }
}

/**
 * Defines a period
 */
data class Period(
    val type: PeriodType,
    val count: Int
) {
    val duration: Duration get() = type.baseDuration * count

    fun humanString(): String {
        return "${count}${type.short}"
    }

    /**
     * Build a day-aligned [TimeRange] from this period, for use with [dateRangeSelect]. Callers
     * that need to supply a default selection should prefer this over the [TimeRange] constructor
     * so the day alignment isn't accidentally dropped.
     */
    fun dayRange(): TimeRange = TimeRange(this, dayAligned = true)
}

/**
 * A list of relative durations for display. We can likely build something more intelligent
 * so that the name is implied from the duration.
 */
val defaultRelativeDurations = listOf(
    PeriodType.Hours.periodOf(1),
    PeriodType.Hours.periodOf(3),
    PeriodType.Hours.periodOf(6),
    PeriodType.Hours.periodOf(12),
    PeriodType.Days.periodOf(1),
    PeriodType.Weeks.periodOf(1)
)

/**
 * The relative periods offered by [dateRangeSelect]. Days and weeks only — sub-day periods are
 * meaningless when the range is snapped to calendar-day boundaries.
 */
val defaultDayDurations = listOf(
    PeriodType.Days.periodOf(1),
    PeriodType.Days.periodOf(3),
    PeriodType.Weeks.periodOf(1),
    PeriodType.Weeks.periodOf(2),
    PeriodType.Weeks.periodOf(4)
)

sealed class PeriodType(val type: String, val baseDuration: Duration, val short: String) {
    fun periodOf(count: Int): Period {
        return Period(this, count)
    }

    /**
     * The whole number of calendar days one unit of this period covers, used for day-aligned
     * ranges. Floored at 1 so sub-day types (minutes, hours) degrade to a single day rather than
     * collapsing to a zero-length range.
     */
    val wholeDays: Int get() = baseDuration.inWholeDays.toInt().coerceAtLeast(1)

    data object Minutes : PeriodType("Minutes", 1.minutes, "m")
    data object Hours : PeriodType("Hours", 1.hours, "h")
    data object Days : PeriodType("Days", 1.days, "d")
    data object Weeks : PeriodType("Weeks", 7.days, "w")
    data object Months : PeriodType("Months", 30.days, "mo")

    open fun add(instant: Instant, count: Int): Instant {
        return instant + (baseDuration.inWholeNanoseconds * count).nanoseconds
    }

    open fun subtract(instant: Instant, count: Int): Instant {
        return instant - (baseDuration.inWholeNanoseconds * count).nanoseconds
    }
}

/**
 * The relative durations that are available in the advanced selection
 */
private val advancedRelativeDurations: List<Period> = listOf(
    PeriodType.Minutes.periodOf(5),
    PeriodType.Minutes.periodOf(10),
    PeriodType.Minutes.periodOf(15),
    PeriodType.Minutes.periodOf(30),
    PeriodType.Minutes.periodOf(45),

    PeriodType.Hours.periodOf(1),
    PeriodType.Hours.periodOf(2),
    PeriodType.Hours.periodOf(3),
    PeriodType.Hours.periodOf(6),
    PeriodType.Hours.periodOf(12),

    PeriodType.Days.periodOf(1),
    PeriodType.Days.periodOf(2),
    PeriodType.Days.periodOf(3),
    PeriodType.Days.periodOf(4),
    PeriodType.Days.periodOf(5),
    PeriodType.Days.periodOf(6),

    PeriodType.Weeks.periodOf(1),
    PeriodType.Weeks.periodOf(2),
    PeriodType.Weeks.periodOf(3),
    PeriodType.Weeks.periodOf(4),
)

/**
 * The relative durations available in the advanced selection of the day-granularity variant.
 * Days and weeks only.
 */
private val advancedDayDurations: List<Period> = advancedRelativeDurations.filter {
    it.type == PeriodType.Days || it.type == PeriodType.Weeks
}

/**
 * How precisely a [timeRangeSelect] lets the user pin down a range.
 */
enum class TimeRangeGranularity(
    /** The relative durations offered in the advanced ("Custom") popover for this granularity. */
    internal val advancedDurations: List<Period>
) {
    /** Full date and time selection, down to the minute. */
    Time(advancedRelativeDurations),

    /**
     * Whole calendar days only. Relative options are days and weeks, the absolute section is a
     * date picker with no time-of-day input, and the emitted range runs from the beginning of the
     * first date to the end of the last.
     */
    Day(advancedDayDurations)
}

/**
 * A component for selecting a time range that includes options for relative periods.
 *
 * Renders a row of one-click relative periods plus a "Custom" popover offering either a wider set
 * of relative periods or an explicit absolute range. Emits null when the user clears an absolute
 * selection.
 *
 * @param topLineOptions the relative periods shown as one-click chips.
 * @param selected the currently selected range, or null for no selection.
 * @param granularity whether the range is selected to the minute or to the whole day. See
 *   [dateRangeSelect] for the day-granularity entry point.
 * @param advancedOptions the relative periods listed in the "Custom" popover; defaults to the set
 *   appropriate for [granularity].
 * @param earliest the earliest instant a selected range may **end** at, or null for no limit. The
 *   start is deliberately left free, since a range that only begins before it is still useful, but
 *   earlier end dates are disabled and an absolute range ending before it cannot be applied.
 *   Relative periods are not checked against it, so callers should only offer periods that fit.
 * @param note an optional line of guidance shown in the "Custom" popover, above the actions — for
 *   example why [earliest] greys out some dates. Null shows nothing.
 * @param onUpdate invoked with the new range, or null when the selection is cleared.
 */
@Composable
fun timeRangeSelect(
    topLineOptions: List<Period> = defaultRelativeDurations,
    selected: TimeRange? = null,
    granularity: TimeRangeGranularity = TimeRangeGranularity.Time,
    advancedOptions: List<Period> = granularity.advancedDurations,
    earliest: Instant? = null,
    note: String? = null,
    onUpdate: (TimeRange?) -> Unit
) {
    val dayAligned = granularity == TimeRangeGranularity.Day
    var advancedSelection: AdvancedSelectionType? by remember { mutableStateOf(null) }

    Style(TimeRangeStyles)
    Div({
        classes(TimeRangeStyles.timePeriodSelectionBox)
    }) {
        var hasSelection = false
        if (selected?.absolute != null) {
            // Clicking the selected range reopens the Absolute picker, pre-filled, so it can be
            // edited rather than only cleared. The close icon stops propagation, so it still clears.
            Span({
                classes(TimeRangeStyles.timePeriod, TimeRangeStyles.timePeriodSelected)
                title("Edit range")
                onClick {
                    advancedSelection = AdvancedSelectionType.Absolute
                }
            }) {
                Text(selected.humanString() ?: "")
                svgIcon(SVGIcons.calendar) {
                    size = 16.px
                }

                icon("close") {
                    title = "Clear range"
                    action {
                        onUpdate(null)
                    }
                }
            }
        } else {
            topLineOptions.forEach { availablePeriod ->
                Span({
                    classes(TimeRangeStyles.timePeriod)
                    if (selected?.relative == availablePeriod) {
                        classes(TimeRangeStyles.timePeriodSelected)
                        hasSelection = true
                    } else {
                        onClick {
                            it.preventDefault()
                            onUpdate(TimeRange(availablePeriod, dayAligned = dayAligned))
                        }
                    }
                }) {
                    Text(availablePeriod.humanString())
                }
            }

            Span({
                classes(TimeRangeStyles.timePeriod)
                if (!hasSelection && selected != null) {
                    classes(TimeRangeStyles.timePeriodSelected)
                }
                onClick {
                    advancedSelection = if (selected != null && selected.relative == null) {
                        AdvancedSelectionType.Absolute
                    } else {
                        AdvancedSelectionType.Relative
                    }
                }
            }) {
                Text(buildString {
                    append("Custom")
                    if (!hasSelection) {
                        selected?.humanString()?.let {
                            append(" (${it})")
                        }
                    }
                })
                svgIcon(SVGIcons.calendar) {
                    size = 16.px
                }
            }
        }

        if (advancedSelection != null) {
            Div({
                classes(TimeRangeStyles.advancedSelection)
                onClickOutside {
                    advancedSelection = null
                }
            }) {
                var advancedTimeRange by remember { mutableStateOf<TimeRange?>(null) }
                Div {
                    buttonRadioGroup(advancedSelection?.name ?: "") {
                        AdvancedSelectionType.entries.forEach { selectionType ->
                            option(selectionType.name, selectionType.name)
                        }
                        onChange {
                            advancedSelection = it.let {
                                try {
                                    AdvancedSelectionType.valueOf(it)
                                } catch (t: Throwable) {
                                    AdvancedSelectionType.Relative
                                }
                            }
                        }
                    }
                }

                Div {
                    if (advancedSelection == AdvancedSelectionType.Absolute && dayAligned) {
                        // Date-only selection. State is held as LocalDate so the picker never
                        // exposes a time of day; the emitted range is widened to cover the whole
                        // of both end dates in the globally selected timezone.
                        val zoneId = currentTimeZoneId()
                        var datesSelected: OpenEndedRange<LocalDate> by remember {
                            mutableStateOf(
                                OpenEndedRange(
                                    selected?.absolute?.start?.toLocalDateIn(zoneId),
                                    selected?.absolute?.end?.toLocalDateIn(zoneId)
                                )
                            )
                        }

                        LaunchedEffect(datesSelected, zoneId) {
                            val start = datesSelected.start
                            val end = datesSelected.end
                            if (start != null && end != null) {
                                advancedTimeRange = TimeRange(
                                    absolute = OpenEndedRange(
                                        start.atStartOfDayInZone(zoneId),
                                        end.atEndOfDayInZone(zoneId)
                                    ),
                                    dayAligned = true
                                )
                            }
                        }

                        Div({ classes(TimeRangeStyles.advancedAbsoluteDurationSelection) }) {
                            Div {
                                Label {
                                    Text("Start")
                                }
                                // Constrain each end by the other so an inverted range can't be picked.
                                datePicker(datesSelected.start, OpenEndedRange(null, datesSelected.end)) {
                                    datesSelected = OpenEndedRange(it, datesSelected.end)
                                }
                            }
                            Div {
                                Label {
                                    Text("End")
                                }
                                datePicker(
                                    datesSelected.end,
                                    OpenEndedRange(laterOf(datesSelected.start, earliest?.toLocalDateIn(zoneId)), null)
                                ) {
                                    datesSelected = OpenEndedRange(datesSelected.start, it)
                                }
                            }
                        }
                    } else if (advancedSelection == AdvancedSelectionType.Absolute) {
                        val zoneId = currentTimeZoneId()
                        var rangeSelected: OpenEndedRange<Instant> by remember {
                            mutableStateOf(OpenEndedRange(selected?.absolute?.start, selected?.absolute?.end))
                        }

                        LaunchedEffect(rangeSelected) {
                            advancedTimeRange = if (rangeSelected.start != null && rangeSelected.end != null) {
                                TimeRange(absolute = rangeSelected)
                            } else {
                                null
                            }
                        }

                        Div({ classes(TimeRangeStyles.advancedAbsoluteDurationSelection) }) {
                            Div {
                                Label {
                                    Text("Start")
                                }
                                DateAndTimeInput(
                                    rangeSelected.start,
                                    zoneId,
                                    defaultTime = LocalTime(0, 0),
                                    endOfMinute = false,
                                    minDate = null,
                                    maxDate = rangeSelected.end?.toLocalDateIn(zoneId)
                                ) {
                                    rangeSelected = OpenEndedRange(it, rangeSelected.end)
                                }
                            }
                            Div {
                                Label {
                                    Text("End")
                                }
                                DateAndTimeInput(
                                    rangeSelected.end,
                                    zoneId,
                                    defaultTime = LocalTime(23, 59),
                                    endOfMinute = true,
                                    minDate = laterOf(
                                        rangeSelected.start?.toLocalDateIn(zoneId),
                                        earliest?.toLocalDateIn(zoneId)
                                    ),
                                    maxDate = null
                                ) {
                                    rangeSelected = OpenEndedRange(rangeSelected.start, it)
                                }
                            }
                        }
                    } else if (advancedSelection == AdvancedSelectionType.Relative) {
                        Div({
                            classes(TimeRangeStyles.advancedRelativeDurationSelection)
                        }) {
                            advancedOptions.groupBy { it.type }.map {
                                it.key to it.value
                            }.sortedBy {
                                it.first.baseDuration
                            }.map {
                                it.first to it.second.sortedBy { it.duration }
                            }.forEach { (type, options) ->
                                Div({ classes(TimeRangeStyles.advancedRelativeDurationType) }) {
                                    Label {
                                        Text(type.type)
                                    }
                                    Ul {
                                        options.forEach { option ->
                                            Li({
                                                onClick {
                                                    onUpdate(TimeRange(option, dayAligned = dayAligned))
                                                    advancedSelection = null
                                                }
                                                if (selected?.relative == option) {
                                                    classes(TimeRangeStyles.advancedSelectionPeriodSelected)
                                                }
                                            }) {
                                                Text(option.humanString())
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                if (note != null) {
                    Div({ classes(TimeRangeStyles.advancedSelectionNote) }) {
                        Text(note)
                    }
                }

                Div({
                    classes(TimeRangeStyles.advancedSelectionActions)
                }) {
                    button {
                        enabled = true
                        action {
                            advancedSelection = null
                        }
                        title = "Cancel"
                    }
                    button {
                        primary = true
                        // A browser `min` greys out earlier dates but still accepts a typed one, so
                        // the limit is enforced here as well.
                        enabled = advancedTimeRange?.let { range ->
                            val resolved = range.range()
                            resolved.start <= resolved.endInclusive &&
                                (earliest == null || resolved.endInclusive >= earliest)
                        } ?: false
                        action {
                            advancedTimeRange?.let {
                                onUpdate(it)
                                advancedSelection = null
                            }
                        }
                        title = "Apply"
                    }
                }
            }
        }
    }
}

/**
 * A day-granularity variant of [timeRangeSelect].
 *
 * Offers only day and week relative periods, and a date picker — with no time-of-day input — for
 * the absolute selection. The emitted [TimeRange] still resolves to [Instant]s: it runs from
 * wall-clock midnight on the first selected date through the end of the last selected date, in the
 * globally selected timezone. Relative periods are snapped the same way and counted inclusively of
 * today, so `1d` is today and `1w` is the trailing seven calendar days.
 *
 * Use [Period.dayRange] rather than the [TimeRange] constructor when supplying a default selection,
 * so the day alignment is preserved.
 *
 * @param topLineOptions the relative periods shown as one-click chips.
 * @param selected the currently selected range, or null for no selection.
 * @param onUpdate invoked with the new range, or null when the selection is cleared.
 */
@Composable
fun dateRangeSelect(
    topLineOptions: List<Period> = defaultDayDurations,
    selected: TimeRange? = null,
    onUpdate: (TimeRange?) -> Unit
) {
    timeRangeSelect(
        topLineOptions = topLineOptions,
        selected = selected,
        granularity = TimeRangeGranularity.Day,
        onUpdate = onUpdate
    )
}

/** The later of two optional dates, or whichever one is set. */
private fun laterOf(a: LocalDate?, b: LocalDate?): LocalDate? = when {
    a == null -> b
    b == null -> a
    else -> maxOf(a, b)
}

/**
 * One end of a minute-granularity absolute range, entered as a separate date and time.
 *
 * A single `datetime-local` input reports no value at all until both its date and its time are
 * filled in, so the console could never react to a date being picked, and a user who only chose a
 * date was left with a disabled Apply button and a browser "incomplete" tooltip (UCA-1168).
 * Splitting the two lets picking a date fill in [defaultTime] — 12:00 AM for a start, 11:59 PM for
 * an end — which the user can then change. Clearing the time falls back to [defaultTime] as well,
 * so a chosen date always yields an instant; clearing the date yields null.
 *
 * Read and written in [zoneId] — the app-wide zone from the header picker — so a typed time means
 * the same zone as the event timestamps it is compared against and the range label, and matches
 * the day-granularity picker. It used to be the computer's own zone (UCA-1168).
 *
 * @param value the current instant, or null when nothing is selected.
 * @param zoneId the IANA zone the date and time are entered in.
 * @param defaultTime the time filled in when a date is picked without one.
 * @param endOfMinute when true the emitted instant is the last nanosecond of the selected minute,
 *   so an end of 11:59 PM covers the whole of that minute, matching [atEndOfDayInZone].
 * @param minDate the earliest selectable date, or null for no limit.
 * @param maxDate the latest selectable date, or null for no limit.
 * @param onChange invoked with the new instant, or null when the date is cleared.
 */
@Composable
private fun DateAndTimeInput(
    value: Instant?,
    zoneId: String,
    defaultTime: LocalTime,
    endOfMinute: Boolean,
    minDate: LocalDate?,
    maxDate: LocalDate?,
    onChange: (Instant?) -> Unit
) {
    val initial = value?.toLocalDateTimeIn(zoneId)
    var date by remember(zoneId) { mutableStateOf(initial?.date) }
    var time by remember(zoneId) { mutableStateOf(initial?.let { LocalTime(it.hour, it.minute) }) }

    fun emit() {
        val selectedDate = date
        if (selectedDate == null) {
            onChange(null)
        } else {
            val start = LocalDateTime(selectedDate, time ?: defaultTime).toInstantInZone(zoneId)
            onChange(if (endOfMinute) start + 1.minutes - 1.nanoseconds else start)
        }
    }

    Div({ classes(TimeRangeStyles.dateAndTimeInput) }) {
        datePicker(date, OpenEndedRange(minDate, maxDate)) {
            date = it
            if (it != null && time == null) {
                time = defaultTime
            }
            emit()
        }
        Input(InputType.Time) {
            value(time?.let { formatMinute(it) }.orEmpty())
            onInput {
                // An unparseable value is ignored rather than allowed to crash, like the date input.
                val raw = it.value.ifBlank { null }
                val parsed = raw?.let { runCatching { LocalTime.parse(it) }.getOrNull() ?: return@onInput }
                time = parsed?.let { LocalTime(it.hour, it.minute) } ?: defaultTime.takeIf { date != null }
                emit()
            }
        }
    }
}

/** A time as the `HH:mm` string a time input expects. */
private fun formatMinute(time: LocalTime): String =
    "${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}"

enum class AdvancedSelectionType {
    Absolute,
    Relative,
}

object TimeRangeStyles : StyleSheet() {
    val timePeriodSelectionBox by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        padding(6.px, 12.px)
        borderRadius(SXMUI.boxRadius.value())
        shadowBorder()
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        height(36.px)
        minWidth(180.px)
        position(Position.Relative)
        property("box-sizing", "border-box")
    }

    val timePeriod by style {
        display(DisplayStyle.Flex)
        height(24.px)
        padding(0.px, 8.px)
        gap(4.px)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        borderRadius(6.px)
        whiteSpace("nowrap")
        property("box-sizing", "border-box")
        lineHeight(24.px)

        self + hover style {
            backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
            cursor("pointer")
        }
    }

    val timePeriodSelected by style {
        fontWeight(700)
        color(SXMUI.buttonBackgroundPrimary.value())
    }

    val advancedSelection by style {
        position(Position.Absolute)
        // Without an explicit stacking order this popover paints under later siblings — table
        // headers, adjacent cards — because it creates no stacking context of its own.
        property("z-index", ZIndexLayer.DROPDOWN.toCssZIndex())
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(1.em)
        top(100.percent)
        right(0.px)
        shadowBorder()
        backgroundColor(SXMUI.containerContentBackgroundColor.value())
        padding(1.em)
        marginTop(2.px)
        minWidth(33.em)
        borderRadius(SXMUI.boxRadius.value())
    }

    /** The optional guidance line in the "Custom" popover. */
    val advancedSelectionNote by style {
        color(SXMUI.subtleTextColor.value())
        fontSize(0.9.em)
    }

    val advancedSelectionActions by style {

    }

    val advancedSelectionPeriodSelected by style {
        fontWeight(700)
        color(SXMUI.messageInfoBorder.value())
        backgroundColor(SXMUI.messageInfoBackground.value())
        border(1.px, LineStyle.Solid, SXMUI.messageInfoBorder.value())
    }

    /** The date and time inputs for one end of a minute-granularity range, side by side. */
    val dateAndTimeInput by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        gap(0.5.em)
        alignItems(AlignItems.Center)
    }

    val advancedRelativeDurationSelection by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(1.em)
    }

    val advancedAbsoluteDurationSelection by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(1.em)

        child(self, type("div")).style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            gap(1.em)
            alignItems(AlignItems.Center)

            type("label").style {
                width(100.px)
                fontWeight(700)
            }
        }
    }

    val advancedRelativeDurationType by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        gap(1.em)
        alignItems(AlignItems.Center)

        type("label").style {
            width(100.px)
            fontWeight(700)
        }

        type("ul").style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            padding(0.px)
            margin(0.px)
            listStyle("none")
            gap(10.px)

            type("li").style {
                width(3.5.em)
                margin(0.px)
                shadowBorder()
                borderRadius(5.px)
                textAlign("center")
                padding(.6.em)
                fontSize(.9.em)
                lineHeight(.9.em)
                cursor("pointer")

                (self + hover) style {
                    backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
                }

                (self + className("selected")) style {
                    fontWeight(700)
                }
            }
        }
    }
}

data class AllowedIntervals(
    val days: List<Int> = listOf(1, 2, 3, 5, 7),
    val hours: List<Int> = listOf(1, 2, 4, 6, 8, 12),
    val minutes: List<Int> = listOf(1, 5, 10, 15, 30)
)

fun snapToAllowed(value: Double, allowed: List<Int>): Int {
    return allowed.minByOrNull { kotlin.math.abs(it - value) } ?: allowed.first()
}

/**
 * For a given duration, calculates the length of the grouping for graphed data.
 * @param groupCount the suggested group count. We'll attempt to return a duration that is close to duration/groupCount
 * @param allowed the set of allowed intervals.
 */
fun roundGrouping(
    duration: Duration,
    groupCount: Int = 25,
    allowed: AllowedIntervals = AllowedIntervals()
): Duration {
    val group = duration / groupCount

    val groupDays = group.toDouble(DurationUnit.DAYS)
    if (groupDays >= 1.0) {
        // Here you could also snap to allowed days if you want even finer control.
        return (groupDays.roundToLong()).days
    }

    val groupHours = group.toDouble(DurationUnit.HOURS)
    if (groupHours >= 1.0) {
        val bestHour = snapToAllowed(groupHours, allowed.hours)
        return bestHour.hours
    }

    val groupMinutes = group.toDouble(DurationUnit.MINUTES)
    if (groupMinutes >= 1.0) {
        val bestMinute = snapToAllowed(groupMinutes, allowed.minutes)
        return bestMinute.minutes
    }

    val groupSeconds = group.toDouble(DurationUnit.SECONDS)
    return (groupSeconds.roundToLong()).seconds
}
